import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "../Modal";
import { brandsService } from "../../services/brands";
import ScriptOutputView from "../script/ScriptOutputView";
import EditScriptModal from "./EditScriptModal";
import { scriptDetailPath } from "../../utils/scriptRoutes";

const ScriptDetailModal = ({
  scriptHash,
  initialScript,
  brandHash,
  showVideos = false,
  showCreateOnMount = false,
  onClose,
  onScriptRegenerated,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [script, setScript] = useState(initialScript ?? null);
  const [loading, setLoading] = useState(!initialScript);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSceneIndex, setEditSceneIndex] = useState(null);

  const refreshScript = useCallback(() => {
    if (!scriptHash) return;
    brandsService.getScript(
      scriptHash,
      (response) => {
        const nextScript = response?.data ?? null;
        if (nextScript?.brand_hash && brandHash && nextScript.brand_hash !== brandHash) {
          return;
        }
        setScript(nextScript);
      },
      () => {},
    );
  }, [scriptHash, brandHash]);

  useEffect(() => {
    if (!scriptHash) return;
    if (initialScript && initialScript.hash === scriptHash) {
      setScript(initialScript);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    brandsService.getScript(
      scriptHash,
      (response) => {
        const nextScript = response?.data ?? null;
        if (nextScript?.brand_hash && brandHash && nextScript.brand_hash !== brandHash) {
          setError("This script is not part of this brand.");
          setScript(null);
          setLoading(false);
          return;
        }
        setScript(nextScript);
        setLoading(false);
      },
      () => {
        setError("Could not load script details.");
        setLoading(false);
      },
    );
  }, [scriptHash, initialScript, brandHash]);

  const handleEditSuccess = (newScript) => {
    setEditModalOpen(false);
    setEditSceneIndex(null);
    onScriptRegenerated?.(newScript);
  };

  const handleEditScene = (scene, index) => {
    setEditSceneIndex(index);
    setEditModalOpen(true);
  };

  const clearCreateParam = () => {
    if (new URLSearchParams(location.search).get("create") === "1") {
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    }
  };

  const goToScript = () => {
    navigate({
      pathname: scriptDetailPath(scriptHash),
      search: "",
    });
  };

  const goToVideos = (create = false) => {
    navigate({
      pathname: `/scripts/${encodeURIComponent(scriptHash)}/videos/`,
      search: create ? "?create=1" : "",
    });
  };

  const handleOpenVideos = () => {
    if (showVideos) {
      navigate({
        pathname: `/scripts/${encodeURIComponent(scriptHash)}/videos/`,
        search: "?create=1",
      });
      return;
    }
    goToVideos(!script?.video_count);
  };

  const editScene =
    editSceneIndex != null && script?.script_data?.scenes?.[editSceneIndex]
      ? script.script_data.scenes[editSceneIndex]
      : null;

  return (
    <Modal onClose={onClose} maxWidth="max-w-4xl" heightClass="h-[90vh] max-h-[90vh]">
      <ScriptOutputView
        script={script}
        loading={loading}
        error={error}
        onClose={onClose}
        showClose
        onEdit={() => { setEditSceneIndex(null); setEditModalOpen(true); }}
        onEditScene={handleEditScene}
        onOpenVideos={handleOpenVideos}
        onOpenScript={goToScript}
        showVideos={showVideos}
        showCreateOnMount={showCreateOnMount}
        onVideoCreated={refreshScript}
        onClearCreateParam={clearCreateParam}
        videoCount={script?.video_count || 0}
      />
      {editModalOpen && script && (
        <EditScriptModal
          script={script}
          sceneIndex={editSceneIndex}
          scene={editScene}
          onClose={() => { setEditModalOpen(false); setEditSceneIndex(null); }}
          onSuccess={handleEditSuccess}
        />
      )}
    </Modal>
  );
};

export default ScriptDetailModal;
